/**
 * /verify-phone — standalone post-registration phone verification step (ADR-0018 §3).
 *
 * Two-step form:
 *   1. Country selector + national phone number entry → sends OTP
 *   2. 6-digit OTP entry with 60 s resend cooldown
 *
 * Skip copy is neutral (no dark patterns, no countdowns):
 * "You can check the current Premium trial offer whenever you verify — everything else works now."
 *
 * SEAM NOTE (ISSUE-011): This page is currently reachable via Settings → Phone.
 * ISSUE-011's onboarding flow will redirect users here automatically after
 * their first sign-in once that flow is built.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { friendlyErrorMessage } from '@nutians/shared';
import {
  EU_EEA_UK_COUNTRIES,
  maskPhoneForDisplay,
  type SendOtpForm,
  sendOtpFormSchema,
  type VerifyOtpForm,
  verifyOtpFormSchema,
} from '@nutians/shared/schemas';
import { useQueryClient } from '@tanstack/react-query';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { sendPhoneOtp, verifyPhoneOtp } from '../auth.js';
import Turnstile from '../components/Turnstile.js';
import { ENTITLEMENT_KEY } from '../hooks/useEntitlement.js';

const RESEND_COOLDOWN_SECONDS = 60;

/** Best-guess default calling code from the browser locale. */
function defaultCallingCode(): string {
  const locale = navigator.language ?? 'en-GB';
  const region = locale.split('-')[1]?.toUpperCase();
  const match = EU_EEA_UK_COUNTRIES.find((c) => c.isoCode === region);
  return match?.callingCode ?? '+44';
}

export default function VerifyPhonePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [e164Phone, setE164Phone] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Phone form ────────────────────────────────────────────────────────────
  const phoneForm = useForm<SendOtpForm>({
    resolver: zodResolver(sendOtpFormSchema),
    defaultValues: { callingCode: defaultCallingCode(), nationalNumber: '' },
  });

  // ── OTP form ──────────────────────────────────────────────────────────────
  const otpForm = useForm<VerifyOtpForm>({
    resolver: zodResolver(verifyOtpFormSchema),
    defaultValues: { code: '' },
  });

  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(
    () => () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    },
    [],
  );

  async function onSendOtp(data: SendOtpForm) {
    setServerError(null);
    const raw = `${data.callingCode}${data.nationalNumber.replace(/\s/g, '')}`;
    const parsed = parsePhoneNumberFromString(raw);
    const phone = parsed?.format('E.164') ?? raw;
    const { error } = await sendPhoneOtp(phone, turnstileToken);
    if (error) {
      setServerError(friendlyErrorMessage(error, 'Could not send the code. Please try again.'));
      setTurnstileResetKey((k) => k + 1);
      return;
    }
    setE164Phone(phone);
    setStep('otp');
    startCooldown();
  }

  async function onVerifyOtp(data: VerifyOtpForm) {
    setServerError(null);
    const { error } = await verifyPhoneOtp(e164Phone, data.code, { updatePhoneNumber: true });
    if (error) {
      setServerError(friendlyErrorMessage(error, 'Invalid code. Please try again.'));
      otpForm.resetField('code');
      return;
    }
    await qc.invalidateQueries({ queryKey: ENTITLEMENT_KEY });
    setVerified(true);
  }

  const handleResend = useCallback(async () => {
    if (cooldown > 0) return;
    setServerError(null);
    setTurnstileResetKey((k) => k + 1);
    const { callingCode, nationalNumber } = phoneForm.getValues();
    const raw = `${callingCode}${nationalNumber.replace(/\s/g, '')}`;
    const parsed = parsePhoneNumberFromString(raw);
    const phone = parsed?.format('E.164') ?? raw;
    const { error } = await sendPhoneOtp(phone, turnstileToken);
    if (error) {
      setServerError(friendlyErrorMessage(error, 'Could not resend. Please try again.'));
      return;
    }
    otpForm.resetField('code');
    startCooldown();
    // SR announcement handled via aria-live region in the JSX
  }, [cooldown, phoneForm, otpForm, turnstileToken, startCooldown]);

  if (verified) {
    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-4 px-8 py-12">
        <h1 className="text-2xl font-medium tracking-tight">Phone verified</h1>
        <p className="text-sm text-ink-soft dark:text-ink-soft">
          Your phone is verified. You can now check the current Premium trial offer.
        </p>
        <button
          type="button"
          onClick={() => void navigate('/')}
          className="rounded bg-leaf px-3 py-2 text-sm font-medium text-white hover:bg-shell dark:bg-husk dark:text-ink dark:hover:bg-husk"
        >
          Go to home
        </button>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-sm flex-col justify-center gap-6 px-8 py-12">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-medium tracking-tight">
          {step === 'phone' ? 'Verify your phone' : 'Enter the code'}
        </h1>
        <p className="text-sm text-ink-soft dark:text-ink-soft">
          {step === 'phone'
            ? 'A one-time code will be sent by SMS.'
            : `We sent a 6-digit code to ${maskPhoneForDisplay(e164Phone)}.`}
        </p>
      </div>

      {/* ── Step 1: phone entry ───────────────────────────────────────── */}
      {step === 'phone' && (
        <form
          onSubmit={(e) => void phoneForm.handleSubmit(onSendOtp)(e)}
          className="flex flex-col gap-4"
          noValidate
          aria-label="Phone verification"
        >
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor="callingCode" className="font-medium">
              Country
            </label>
            <select
              id="callingCode"
              {...phoneForm.register('callingCode')}
              className="rounded border border-ink/20 px-3 py-2 text-sm dark:border-ink/20 dark:bg-leaf"
              aria-describedby="callingCode-err"
            >
              {EU_EEA_UK_COUNTRIES.map((c) => (
                <option key={c.isoCode} value={c.callingCode}>
                  {c.name} ({c.callingCode})
                </option>
              ))}
            </select>
            {phoneForm.formState.errors.callingCode && (
              <p id="callingCode-err" className="text-xs text-clay dark:text-clay" role="alert">
                {phoneForm.formState.errors.callingCode.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor="nationalNumber" className="font-medium">
              Phone number
            </label>
            <input
              id="nationalNumber"
              type="tel"
              autoComplete="tel-national"
              inputMode="tel"
              placeholder="07911 123456"
              {...phoneForm.register('nationalNumber')}
              className="rounded border border-ink/20 px-3 py-2 text-sm dark:border-ink/20 dark:bg-leaf"
              aria-describedby="nationalNumber-err"
            />
            {phoneForm.formState.errors.nationalNumber && (
              <p id="nationalNumber-err" className="text-xs text-clay dark:text-clay" role="alert">
                {phoneForm.formState.errors.nationalNumber.message}
              </p>
            )}
          </div>

          <Turnstile
            onToken={setTurnstileToken}
            onError={() => setServerError('Bot-protection check failed. Please refresh.')}
            onExpire={() => setTurnstileToken('')}
            resetKey={turnstileResetKey}
          />

          {serverError && (
            <p className="text-sm text-clay dark:text-clay" role="alert" aria-live="assertive">
              {serverError}
            </p>
          )}

          <button
            type="submit"
            disabled={phoneForm.formState.isSubmitting}
            className="rounded bg-leaf px-3 py-2 text-sm font-medium text-white hover:bg-shell disabled:opacity-50 dark:bg-husk dark:text-ink dark:hover:bg-husk"
          >
            {phoneForm.formState.isSubmitting ? 'Sending…' : 'Send code'}
          </button>
        </form>
      )}

      {/* ── Step 2: OTP entry ─────────────────────────────────────────── */}
      {step === 'otp' && (
        <form
          onSubmit={(e) => void otpForm.handleSubmit(onVerifyOtp)(e)}
          className="flex flex-col gap-4"
          noValidate
          aria-label="Enter verification code"
        >
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor="otp-code" className="font-medium">
              6-digit code
            </label>
            <input
              id="otp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              {...otpForm.register('code')}
              className="rounded border border-ink/20 px-3 py-2 text-center text-lg font-mono tracking-widest dark:border-ink/20 dark:bg-leaf"
              aria-describedby="otp-err otp-resend-status"
            />
            {otpForm.formState.errors.code && (
              <p id="otp-err" className="text-xs text-clay dark:text-clay" role="alert">
                {otpForm.formState.errors.code.message}
              </p>
            )}
          </div>

          {serverError && (
            <p className="text-sm text-clay dark:text-clay" role="alert" aria-live="assertive">
              {serverError}
            </p>
          )}

          <button
            type="submit"
            disabled={otpForm.formState.isSubmitting}
            className="rounded bg-leaf px-3 py-2 text-sm font-medium text-white hover:bg-shell disabled:opacity-50 dark:bg-husk dark:text-ink dark:hover:bg-husk"
          >
            {otpForm.formState.isSubmitting ? 'Verifying…' : 'Verify'}
          </button>

          <div id="otp-resend-status" aria-live="polite" className="text-center text-sm">
            {cooldown > 0 ? (
              <span className="text-ink-soft dark:text-ink-soft">
                Resend available in {cooldown}s
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void handleResend()}
                className="font-medium text-ink underline hover:text-ink dark:text-ink dark:hover:text-ink-soft"
              >
                Resend code
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setStep('phone')}
            className="text-center text-sm text-ink-soft underline hover:text-ink dark:text-ink-soft dark:hover:text-ink"
          >
            Change number
          </button>
        </form>
      )}

      {/* ── Skip link ─────────────────────────────────────────────────── */}
      <p className="text-sm text-ink-soft dark:text-ink-soft">
        You can check the current Premium trial offer whenever you verify — everything else works
        now.{' '}
        <Link
          to="/"
          className="font-medium text-ink underline hover:text-ink dark:text-ink dark:hover:text-ink-soft"
        >
          Continue without verifying
        </Link>
      </p>
    </main>
  );
}
